import React from 'react'
import { Box, IconButton } from '@mui/material'
import { Over, useDroppable } from '@dnd-kit/core'
import AddRoundedIcon from '@mui/icons-material/AddRounded'
import ChatBubbleOutlineRoundedIcon from '@mui/icons-material/ChatBubbleOutlineRounded'
import { advancedColors } from '../../theme'
import ItemTag, { TagDecoration } from './ItemTag'
import { dataCellSx } from './gridSx'

export type PlacedItem = {
  id: string
  option: string
  // Set on Modules rows; `option` is then the track title as it read when placed.
  trackKey?: string
  startMonth: number
  months: number
  comment?: string
  // A planned track's lead, used when it starts. Absent: chosen at start, or by the patient when booking.
  lead?: { providerUid: string; joinAs: string | null }
  // Week-exact start (0-based from Month 1's Monday), set when a track is accepted; `startMonth` stays its month.
  startWeek?: number
}

// Only unique, never parsed: a drop's row and position travel in its `data`.
const dropCellId = (rowKey: string, month: number) => `${rowKey}::${month}`

export type DropCell = { rowKey: string; month: number }

export const dropCell = (over: Over): DropCell | undefined => {
  const { rowKey, month } = over.data.current ?? {}

  return typeof rowKey === 'string' && typeof month === 'number' ? { rowKey, month } : undefined
}

type CommentClickHandler = (item: PlacedItem, event: React.MouseEvent<HTMLElement>) => void

type Props = {
  rowKey: string
  // A position on this row's own axis — months, or weeks for Surveys. Opaque here either way.
  month: number
  // The cell's first timeline column; with `colSpan`, places a bar's accepted start on it.
  startColumn?: number
  items: PlacedItem[]
  colSpan: number
  editable: boolean
  decorationFor?: (item: PlacedItem) => TagDecoration | undefined
  onAddClick: (event: React.MouseEvent<HTMLElement>) => void
  onCommentClick: CommentClickHandler
  onRemoveClick: (item: PlacedItem) => void
  // Off where a drag moves by distance instead of by drop cell (the Master Care Plan grid).
  droppable?: boolean
}

const PlannerCell = ({
  rowKey,
  month,
  items,
  colSpan,
  editable,
  decorationFor = () => undefined,
  onAddClick,
  onCommentClick,
  onRemoveClick,
  droppable = true,
  startColumn,
}: Props) => {
  const { setNodeRef, isOver } = useDroppable({
    id: dropCellId(rowKey, month),
    data: { rowKey, month } satisfies DropCell,
    disabled: !editable || !droppable,
  })

  if (items.length === 0) {
    return (
      <Box component="td" colSpan={colSpan} sx={dataCellSx}>
        <Box
          ref={setNodeRef}
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: 40,
            backgroundColor: isOver ? advancedColors.accentSoft : 'transparent',
          }}
        >
          {editable && (
            <IconButton size="small" onClick={onAddClick} aria-label="Add care item">
              <AddRoundedIcon fontSize="small" sx={{ color: advancedColors.inkFaint }} />
            </IconButton>
          )}
        </Box>
      </Box>
    )
  }

  return (
    <Box component="td" colSpan={colSpan} sx={dataCellSx}>
      <Box
        ref={setNodeRef}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 0.5,
          m: 0.5,
          minHeight: 40,
          backgroundColor: isOver ? advancedColors.accentSoft : 'transparent',
        }}
      >
        {items.map((item) => {
          const decoration = decorationFor(item)
          const at =
            startColumn !== undefined && decoration?.acceptedAtColumn !== undefined
              ? (decoration.acceptedAtColumn - startColumn) / colSpan
              : undefined

          return (
            <Box key={item.id} sx={{ display: 'flex', alignItems: 'stretch', gap: 0.25, flex: 1, minHeight: 0 }}>
              <ItemTag
                rowKey={rowKey}
                item={item}
                editable={editable}
                decoration={decoration}
                onRemoveClick={onRemoveClick}
                acceptedAt={at !== undefined && at > 0 && at < 1 ? at : undefined}
              />
              {editable && !decoration?.locked && (
                <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                  <IconButton
                    size="small"
                    onClick={onAddClick}
                    aria-label="Add another care item to this slot"
                    sx={{ p: 0.25 }}
                  >
                    <AddRoundedIcon sx={{ fontSize: 14, color: advancedColors.inkMuted }} />
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={(event) => onCommentClick(item, event)}
                    aria-label="Comment on this item"
                    sx={{ p: 0.25 }}
                  >
                    <ChatBubbleOutlineRoundedIcon sx={{ fontSize: 13, color: advancedColors.inkMuted }} />
                  </IconButton>
                </Box>
              )}
            </Box>
          )
        })}
      </Box>
    </Box>
  )
}

export default PlannerCell
